import { UserSubscriptionRole } from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { curryAppSelector } from "../reselect";
import { SubscriptionFeaturesState } from "./subscription-features-slice";

type SubscriptionFeaturesStore = {
  subscriptionFeatures: SubscriptionFeaturesState;
};

/**
 *
 * @param state the state of the store
 * @returns The subscriptions slice of the store
 */
export function selectSubscriptionFeaturesSlice({
  subscriptionFeatures,
}: SubscriptionFeaturesStore): SubscriptionFeaturesState {
  return subscriptionFeatures;
}

/**
 * @returns true if the subscriptions of the current users are available
 * @param state current app state
 */
export function selectAreSubscriptionsAvailable({
  subscriptionFeatures,
}: SubscriptionFeaturesStore): boolean {
  return subscriptionFeatures.featuresAvailable !== undefined;
}

/**
 * @returns Whether a feature is available, enabled and permitted for the current project and current user.
 * This should be the default permission check for features in the app. Knowing the other states should only be needed,
 * if you want to display more information about why the features is not usable.
 * @param state current app state
 * @param feature the feature to check
 */
const selectIsFeatureEnabledAndPermitted = curryAppSelector(
  createSelector(
    (state: SubscriptionFeaturesStore) =>
      state.subscriptionFeatures.featuresAvailable,
    (_: SubscriptionFeaturesStore, feature: UserSubscriptionRole) => feature,
    (featuresAvailable, feature) =>
      !!featuresAvailable?.find(
        (f) => f.identifier === feature && f.enabled && f.permitted,
      ),
  ),
);

/**
 * @returns Whether a feature is available and enabled for the current company.
 * Company features describe the state of the workspace rather than an action of the current user,
 * so the per-user `permitted` flag is not taken into account.
 * @param state current app state
 * @param feature the feature to check
 */
const selectIsCompanyFeatureEnabled = curryAppSelector(
  createSelector(
    (state: SubscriptionFeaturesStore) =>
      state.subscriptionFeatures.companyFeaturesAvailable,
    (_: SubscriptionFeaturesStore, feature: UserSubscriptionRole) => feature,
    (companyFeaturesAvailable, feature) =>
      !!companyFeaturesAvailable?.find(
        (f) => f.identifier === feature && f.enabled,
      ),
  ),
);

/**
 * @returns true if the current company has the proper bundle to upload a point cloud
 * @param state current app state
 */
export function selectCanUploadPointCloud(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.globalPointcloudWrite,
  )(state);
}

/**
 * @returns true if the current company has the proper bundle to upload data via Data Management
 *          to create a point cloud
 * @param state current app state
 */
export function selectCanUploadWithDataManagement(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.sphereXgAddonPointCloud,
  )(state);
}

/**
 * @returns true if the current company has the proper bundle to read a point cloud
 * @param state current app state
 */
export function selectCanReadPointCloud(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.globalPointcloudRead,
  )(state);
}

/**
 * @returns true if the current company has the proper bundle to upload and process Orbis files
 * @param state current app state
 */
export function selectCanUploadOrbisFiles(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.globalOrbisProcessing,
  )(state);
}

/**
 * @returns true if the current company has the proper bundle to upload and process Focus files
 * @param state current app state
 */
export function selectCanUploadFocusFiles(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.focusProcessing,
  )(state);
}

/**
 * @returns true if the current company has the proper bundle to upload CAD
 * @param state current app state
 */
export function selectCanUploadCad(state: SubscriptionFeaturesStore): boolean {
  return selectIsFeatureEnabledAndPermitted(UserSubscriptionRole.cadImport)(
    state,
  );
}

/**
 * @returns true if the current company has the proper bundle to display the CAD's mesh and its metadata
 * @param state current app state
 */
export function selectCanReadCAD(state: SubscriptionFeaturesStore): boolean {
  return selectIsFeatureEnabledAndPermitted(UserSubscriptionRole.cadDisplay)(
    state,
  );
}

/**
 * @returns true if the user is allowed to generate floorplans
 * @param state current app state
 */
export function selectCanGenerateFloorplans(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.floorplanGenerator,
  )(state);
}

/**
 * @returns true if all members of the company are allowed to export point clouds
 * @param state current app state
 */
export function selectCanAllMembersExportPointCloud(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.pcExportAllMembers,
  )(state);
}

/**
 * @returns true if the current company has the proper bundle to use the object detection feature
 * @param state current app state
 */
export function selectCanUseObjectDetection(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.objectDetection,
  )(state);
}

/**
 * @returns true if the AI indexing is enabled for this project
 * @param state current app state
 */
export function selectCanUseAiIndexing(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(UserSubscriptionRole.aiIndexing)(
    state,
  );
}

/**
 * @returns true if the AI indexing is enabled for this project
 * @param state current app state
 */
export function selectCanUseSnapshotCreation(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.globalSnapshotCreate,
  )(state);
}

/**
 * @returns true if the photogrammetry (drone) feature is available for the current company
 * @param state current app state
 */
export function selectCanUsePhotogrammetry(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsFeatureEnabledAndPermitted(
    UserSubscriptionRole.photogrammetry,
  )(state);
}

/**
 * @returns true if the current workspace has been migrated from HoloBuilder to Sphere XG
 * @param state current app state
 */
export function selectIsHbToSxgMigrated(
  state: SubscriptionFeaturesStore,
): boolean {
  return selectIsCompanyFeatureEnabled(UserSubscriptionRole.hbToSxgMigrated)(
    state,
  );
}
